import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { boardAPI, postAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';

const PostEditContainer = styled.div`
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.text};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-family: inherit;
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-family: inherit;
  background-color: white;
`;

const EditorContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  .quill {
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.colors.border};
  }
  
  .ql-editor {
    min-height: 300px;
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  background-color: ${({ theme, variant }) => 
    variant === 'primary' ? theme.colors.primary : 'white'};
  color: ${({ theme, variant }) => 
    variant === 'primary' ? 'white' : theme.colors.text};
  border: 1px solid ${({ theme, variant }) => 
    variant === 'primary' ? theme.colors.primary : theme.colors.border};

  &:hover {
    background-color: ${({ theme, variant }) => 
      variant === 'primary' ? theme.colors.info : theme.colors.light};
  }
`;

interface Board {
  id: number;
  name: string;
}

interface Params {
  postId: string;
  [key: string]: string;
}

const PostEdit: React.FC = () => {
  const { postId } = useParams<Params>();
  const navigate = useNavigate();
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedBoard, setSelectedBoard] = useState('');
  const [boards, setBoards] = useState<Board[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authorId, setAuthorId] = useState<number | null>(null);
  
  // authAPI 대신 useAuth 사용
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    const checkLoginAndPermission = async () => {
      // 로그인 상태가 아니면 로그인 페이지로 리다이렉트
      if (!isAuthenticated) {
        alert('로그인이 필요합니다.');
        navigate('/login');
        return;
      }
    };

    const fetchBoards = async () => {
      try {
        const boardsData = await boardAPI.getBoards();
        setBoards(boardsData.data);
      } catch (error) {
        console.error('게시판 목록 로딩 오류:', error);
      }
    };

    const fetchPost = async () => {
      try {
        const postData = await postAPI.getPostById(Number(postId));
        setTitle(postData.data.title);
        setContent(postData.data.content);
        setSelectedBoard(postData.data.board.id.toString());
        setAuthorId(postData.data.author.id);
      } catch (error) {
        console.error('게시글 로딩 오류:', error);
        navigate('/');
      }
    };

    checkLoginAndPermission();
    fetchBoards();
    fetchPost();
  }, [postId, navigate, isAuthenticated]);

  useEffect(() => {
    // 게시글을 불러온 후 작성자와 현재 사용자가 다르면 리다이렉트
    if (user && authorId !== null && user.id !== authorId) {
      alert('자신이 작성한 게시글만 수정할 수 있습니다.');
      navigate(`/posts/${postId}`);
    }
  }, [user, authorId, postId, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !content.trim() || !selectedBoard) {
      alert('제목, 내용, 게시판을 모두 입력해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      await postAPI.updatePost(Number(postId), {
        title,
        content,
        boardId: parseInt(selectedBoard, 10)
      });
      navigate(`/posts/${postId}`);
    } catch (error) {
      console.error('게시글 수정 오류:', error);
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/posts/${postId}`);
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  if (!isAuthenticated || !authorId) {
    return <div>로딩 중...</div>;
  }

  return (
    <PostEditContainer>
      <PageTitle>게시글 수정</PageTitle>
      
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="board">게시판</Label>
          <Select
            id="board"
            value={selectedBoard}
            onChange={(e) => setSelectedBoard(e.target.value)}
            required
          >
            <option value="">게시판을 선택해주세요</option>
            {boards.map(board => (
              <option key={board.id} value={board.id.toString()}>
                {board.name}
              </option>
            ))}
          </Select>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="title">제목</Label>
          <Input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="제목을 입력해주세요"
            required
          />
        </FormGroup>
        
        <EditorContainer>
          <Label htmlFor="content">내용</Label>
          <ReactQuill
            value={content}
            onChange={setContent}
            modules={modules}
            placeholder="내용을 입력해주세요"
          />
        </EditorContainer>
        
        <ButtonGroup>
          <Button type="button" onClick={handleCancel}>취소</Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? '수정 중...' : '수정하기'}
          </Button>
        </ButtonGroup>
      </form>
    </PostEditContainer>
  );
};

export default PostEdit;